import { useEffect, useEffectEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useWorkingHours } from '@hooks/useWorkingHours';
import { PauseIcon, PlayIcon } from '@utils/icons';
import { formatYmd } from '@utils/dateUtils';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import type { IReplayState } from '../hooks/useReplayState';
import type { IHeldDay } from './useHeldDay';
import { usePlayback, type ReplaySpeed } from './usePlayback';
import { useReplayKeys } from './useReplayKeys';
import { ReplayDayPicker } from './ReplayDayPicker';
import { LiveButton } from './LiveButton';
import { DayTrack } from './DayTrack';
import { shownTrack } from './shownTrack';
import { dayScale } from './trackScale';
import styles from './ReplayBar.module.css';

export interface IReplayBarProps {
  siteId: string;
  siteName: string;
  tz: string;
  state: IReplayState;
  /** The selected day's replay (today's while live); undefined while it loads. */
  replay: IMonitorReplay | undefined;
  /** The selected day is loading. */
  loading: boolean;
  /** While it loads, the day that was on screen (its track shows dimmed); null when there is none. */
  held: IHeldDay | null;
  /** The local playhead; null while live (the bar then sits at the data end). */
  atMs: number | null;
  onAt(ms: number): void;
  playing: boolean;
  onPlayingChange(next: boolean): void;
  /** Kept with the caller, so it survives the bar moving in and out of fullscreen. */
  speed: ReplaySpeed;
  onSpeedChange(speed: ReplaySpeed): void;
  /** Fullscreen with the bar collapsed: where its play button goes meanwhile. */
  collapsedPlaySlot?: HTMLElement | null;
}

/** A marker click lands this long before the event, then plays into it. */
const MARKER_LEAD_MS = 4 * 60_000;
const SPEEDS: ReplaySpeed[] = [1, 10, 30, 60];

/**
 * The replay time bar: play/pause, the site clock and day, speed and day
 * pickers, LIVE, and the day track. It runs the playback clock at the caller's speed;
 * the playhead itself lives with the caller (`atMs` / `onAt`), and the URL is
 * written through `state` only on commit, never per tick.
 */
export function ReplayBar({
  siteId,
  siteName,
  tz,
  state,
  replay,
  loading,
  held,
  atMs,
  onAt,
  playing,
  onPlayingChange,
  speed,
  onSpeedChange,
  collapsedPlaySlot,
}: IReplayBarProps) {
  const { t, i18n } = useTranslation();
  const { data: workingHours } = useWorkingHours(siteId);

  const now = DateTime.now().setZone(tz);
  const today = formatYmd(now, tz);
  const live = state.date === null && atMs === null;
  // The track's own clamp: a finished day's dataEnd is the next midnight, past the window end.
  const scale = replay && dayScale(replay);
  const range = scale ? { start: scale.start, end: scale.dataEnd } : null;
  const endMs = range?.end ?? now.toMillis();
  const at = atMs ?? state.atMs ?? endMs;
  const day = state.date ?? today;
  // Without a day loaded the play button is disabled, so this only matters with a range.
  const startMs = range?.start ?? at;

  const shown = shownTrack(replay, at, held, loading);

  const goLive = () => {
    onPlayingChange(false);
    state.close();
    state.openBar();
  };
  const seek = (ms: number, commit: boolean) => {
    onPlayingChange(false);
    onAt(ms);
    if (commit) state.commitAt(ms);
  };
  const toggle = (start: number) => {
    if (playing) {
      onPlayingChange(false);
      state.commitAt(at);
      return;
    }
    // From live, play the day from its start.
    if (live) {
      onAt(start);
      state.commitAt(start);
    }
    onPlayingChange(true);
  };
  const pick = (date: string) => {
    if (date === today) goLive();
    else {
      onPlayingChange(false);
      state.selectDate(date);
    }
  };
  const onMarker = (e: IReplayEvent) => {
    const ms = Math.max(Date.parse(e.at) - MARKER_LEAD_MS, startMs);
    onAt(ms);
    state.commitAt(ms);
    onPlayingChange(true);
  };

  usePlayback({
    atMs: at,
    endMs,
    speed,
    playing: playing && range !== null,
    onTick: onAt,
    onEnd: () => {
      if (day === today) goLive();
      else {
        onPlayingChange(false);
        state.commitAt(endMs);
      }
    },
  });

  // A newly picked day arrives at its site-local midnight (or before the
  // window); move to the day's first in, or the window start without presence.
  // An explicit time (a deep link) is past both and is left alone. Runs when
  // the day's data (or live-ness) changes, never per playhead tick.
  const arriveAtFirstIn = useEffectEvent(() => {
    if (!range || live || !replay) return;
    const midnight = DateTime.fromISO(replay.date, { zone: tz }).startOf('day').toMillis();
    if (at !== midnight && at >= range.start) return;
    const target = replay.firstIn ? Date.parse(replay.firstIn) : range.start;
    if (target === at) return;
    onAt(target);
    state.commitAt(target);
  });
  useEffect(() => {
    arriveAtFirstIn();
  }, [replay, live]);

  useReplayKeys({
    range,
    atMs: at,
    onClose: () => {
      onPlayingChange(false);
      state.close();
    },
    onToggle: toggle,
    onSeek: (ms) => seek(ms, true),
  });

  const atSite = DateTime.fromMillis(at, { zone: tz }).setLocale(i18n.language);
  const playButton = (
    <button
      type="button"
      className={styles.play}
      aria-label={t(playing ? 'monitor.replay.pause' : 'monitor.replay.play')}
      disabled={!range}
      onClick={() => toggle(startMs)}
    >
      {playing ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
    </button>
  );
  return (
    <div className={styles.bar} role="region" aria-label={t('monitor.replay.replay')} aria-busy={loading || undefined}>
      <div className={styles.row}>
        {!collapsedPlaySlot && playButton}
        <span className={styles.clock}>{atSite.toFormat('HH:mm')}</span>
        <span className={styles.date}>
          <b>{atSite.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' })}</b>
          {siteName}
        </span>
        {!live && <span className={styles.tag}>{t('monitor.replay.tag')}</span>}
        <SegmentedControl
          size="sm"
          ariaLabel={t('monitor.replay.speedLabel')}
          value={String(speed)}
          onChange={(value) => onSpeedChange(Number(value) as ReplaySpeed)}
          options={SPEEDS.map((option) => ({ id: String(option), label: t('monitor.replay.speed', { speed: option }) }))}
        />
        <span className={styles.hint}>
          <kbd>{t('monitor.replay.keySpace')}</kbd> {t('monitor.replay.hintPlay')} · <kbd>←</kbd>
          <kbd>→</kbd> {t('monitor.replay.hintStep')} · <kbd>{t('monitor.replay.keyEsc')}</kbd>{' '}
          {t('monitor.replay.hintClose')}
        </span>
        <span className={styles.spacer} />
        <ReplayDayPicker now={now} day={day} workingHours={workingHours} onPick={pick} />
        <LiveButton live={live} onClick={goLive} />
      </div>
      {shown && <DayTrack shown={shown} workingHours={workingHours} onSeek={seek} onMarker={onMarker} />}
      {loading && <span className={styles.loadLine} aria-hidden data-testid="replay-loading" />}
      {collapsedPlaySlot && createPortal(playButton, collapsedPlaySlot)}
    </div>
  );
}
